import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { Spin } from 'antd';
import {
  getCustomTables,
  getStatuses,
  getTemplates,
} from '../forms/state/forms.actions';
import { getBillingRates } from '../billingRates/state/billingRates.actions';
import { getCustomers } from '../contacts/customers/state/customers.actions';
import { getUserTeams } from '../users/state/users.actions';
import { getProjectEquipment } from '../projects/state/projects.actions';
import { getEquipment } from '../equipment/state/equipment.actions';
import { getCustomFields } from '../timecards/state/timecards.actions';
import { getLabels } from '../labels/state/labels.actions';
import { getBoardCardTemplates, getBoards } from '../boards/state/boards.actions';
import useToggle from '../common/hooks/useToggle';
import FormsSearchDrawer from './FormsSearchDrawer';
import FormsList from '../forms/CompletedForms/FormsList';
import OnTraccrButton from '../common/buttons/OnTraccrButton';
import { getBuckets } from '../buckets/state/buckets.actions';
import AdvancedSearchAnalyticsDrawer from './AdvancedSearchAnalyticsDrawer';
import SearchAnalyticsFilterBanner from './SearchAnalyticsFilterBanner';

export default function FormsSearch() {
  const dispatch = useDispatch();
  const results = useSelector((state) => state.search.formResults);
  const formFieldValues = useSelector((state) => state.search.formFieldValues);
  const selectedQuery = useSelector((state) => state.search.selectedQuery);
  const currentQuery = useSelector((state) => state.search.currentQuery);

  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [analyticsFilter, setAnalyticsFilter] = useState(null);

  const { isToggled: drawerVisible, toggle: toggleVisible } = useToggle();
  const { isToggled: analyticsDrawerVisible, toggle: toggleAnalyticsDrawerVisible } = useToggle();

  const onClose = useCallback(async (promise) => {
    toggleVisible();
    if (promise) {
      setSearching(true);
      await promise;
      setSearching(false);
    }
  }, [toggleVisible]);

  useEffect(() => {
    const get = async () => {
      setLoading(true);
      await Promise.all([
        dispatch(getTemplates()),
        dispatch(getBillingRates()),
        dispatch(getStatuses()),
        dispatch(getCustomers()),
        dispatch(getCustomTables()),
        dispatch(getUserTeams()),
        dispatch(getEquipment()),
        dispatch(getProjectEquipment()),
        dispatch(getCustomFields()),
        dispatch(getBoards()),
        dispatch(getBoardCardTemplates()),
        dispatch(getLabels()),
        dispatch(getBuckets()),
      ]);
      setLoading(false);
    };
    get();
  }, []);

  const filteredResults = useMemo(() => {
    if (!analyticsFilter) return results;
    const { fieldId, value } = analyticsFilter;
    return results.filter((form) => formFieldValues[form.id]?.[fieldId] === value);
  }, [results, formFieldValues, analyticsFilter]);

  useEffect(() => {
    setAnalyticsFilter(null);
  }, [results]);

  const SearchButton = useMemo(() => (
    <>
      <OnTraccrButton
        title="Open Search"
        icon={<SearchOutlined />}
        style={{ marginLeft: 10 }}
        onClick={toggleVisible}
        disabled={loading || searching}
      />
      {(loading || searching) ?? <Spin /> }
    </>
  ), [toggleVisible, loading, searching]);

  const hasAnalyticsFields = useMemo(() => {
    const queryToUse = currentQuery ?? selectedQuery;
    return queryToUse?.type === 'forms'
      && (queryToUse?.query?.formulas?.filter((f) => f.analytics).length ?? 0) > 0;
  }, [selectedQuery, currentQuery]);

  return (
    <div>
      <SearchAnalyticsFilterBanner
        activeFilter={analyticsFilter}
        filteredCount={filteredResults.length}
        totalCount={results.length}
        onClear={() => setAnalyticsFilter(null)}
      />
      <FormsList
        forms={filteredResults}
        hideDate
        customSearch={SearchButton}
        skipLoad
        parentLoading={loading || searching}
        shouldGetTemplates={false}
        onAnalyticsClick={hasAnalyticsFields ? toggleAnalyticsDrawerVisible : null}
      />
      <FormsSearchDrawer
        visible={drawerVisible}
        onClose={onClose}
      />
      {results.length > 0 && hasAnalyticsFields && (
        <AdvancedSearchAnalyticsDrawer
          visible={analyticsDrawerVisible}
          onClose={toggleAnalyticsDrawerVisible}
          activeFilter={analyticsFilter}
          onFilterChange={setAnalyticsFilter}
        />
      )}
    </div>
  );
}
